import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  Add as AddIcon,
  ViewList as ListIcon,
  ViewModule as GridIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  deleteDoc
} from 'firebase/firestore';

// Importy komponentów
import GoalCard from '../../components/Goals/GoalCard';
import TeamGoalCard from '../../components/Goals/TeamGoalCard';
import GoalDetails from '../../components/Goals/GoalDetails';
import GoalDetailsModal from '../../components/Goals/GoalDetailsModal';
import GoalForm from '../../components/Goals/GoalForm';
import GoalsFilters from '../../components/Goals/GoalsFilters';
import GoalsDashboard from '../../components/Goals/GoalsDashboard';

// Importy stałych i pomocniczych funkcji
import { 
  getTargetTypeLabel, 
  getStatusLabel, 
  getPeriodLabel,
  getStatusColor,
  getProgressColor
} from '../../constants/goalsConstants';

const GoalsPage = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  
  // Stan dla celów
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Stan dla pracowników (potrzebny do filtrów i formularza)
  const [employees, setEmployees] = useState([]);
  
  // Stan dla paginacji
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  
  // Stan dla wybranego celu (edycja, szczegóły, usuwanie)
  const [selectedGoal, setSelectedGoal] = useState(null);
  
  // Stan dla dialogów
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Stan dla powiadomień
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Stan dla filtrów
  const [filters, setFilters] = useState({
    year: 'all',
    month: 'all',
    quarter: 'all',
    targetType: 'all',
    assignedTo: 'all',
    status: 'all',
    onlyCompanyGoals: false,
    onlyIndividualGoals: false
  });
  
  // Stan dla widoku (karty/tabela)
  const [viewMode, setViewMode] = useState('cards');
  
  // Stan dla aktywnej zakładki
  const [activeTab, setActiveTab] = useState(0);

  // Pobierz dane przy pierwszym renderowaniu
  useEffect(() => {
    fetchGoals();
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin, currentUser]);

  // Filtruj cele przy zmianie filtrów
  useEffect(() => {
    applyFilters();
  }, [goals, filters]);

  // Funkcja pobierająca cele
  const fetchGoals = async () => {
    try {
      setLoading(true);
      let goalsQuery;
      
      if (isAdmin) {
        // Administrator widzi wszystkie cele
        goalsQuery = query(
          collection(db, 'goals'),
          orderBy('year', 'desc'),
          orderBy('month', 'desc')
        );
      } else {
        // Zwykły pracownik widzi tylko swoje cele i cele firmowe
        goalsQuery = query(
          collection(db, 'goals'),
          where('assigned_to', 'in', [currentUser.uid, null])
        );
      }
      
      const goalsSnapshot = await getDocs(goalsQuery);
      const goalsList = await Promise.all(goalsSnapshot.docs.map(async docSnapshot => {
        const goalData = docSnapshot.data();
        
        // Pobierz dane o przypisanym pracowniku (jeśli istnieje)
        let assignedToName = '';
        if (goalData.assignedTo) {
          try {
            const userRef = doc(db, 'users', goalData.assignedTo);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              assignedToName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
            }
          } catch (error) {
            console.error(`Błąd podczas pobierania danych pracownika:`, error);
          }
        }
        
        return {
          id: docSnapshot.id,
          ...goalData,
          startDate: goalData.startDate?.toDate() || new Date(),
          endDate: goalData.endDate?.toDate() || new Date(),
          createdAt: goalData.createdAt?.toDate() || new Date(),
          lastUpdate: goalData.lastUpdate?.toDate() || new Date(),
          assignedToName
        };
      }));
      
      setGoals(goalsList);
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas pobierania celów:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania celów',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Funkcja pobierająca pracowników
  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        firstName: docSnapshot.data().firstName || '',
        lastName: docSnapshot.data().lastName || '',
        email: docSnapshot.data().email || '',
        role: docSnapshot.data().role || 'user'
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
      setEmployees([]);
    }
  };

  // Funkcja filtrująca cele
  const applyFilters = () => {
    let filtered = [...goals];
    
    // Filtrowanie po roku
    if (filters.year !== 'all') {
      filtered = filtered.filter(goal => goal.year === parseInt(filters.year));
    }
    
    // Filtrowanie po miesiącu
    if (filters.month !== 'all') {
      filtered = filtered.filter(goal => goal.month === parseInt(filters.month));
    }
    
    // Filtrowanie po kwartale
    if (filters.quarter !== 'all') {
      filtered = filtered.filter(goal => goal.quarter === parseInt(filters.quarter));
    }
    
    // Filtrowanie po typie celu
    if (filters.targetType !== 'all') {
      filtered = filtered.filter(goal => goal.targetType === filters.targetType);
    }
    
    // Filtrowanie po statusie
    if (filters.status !== 'all') {
      filtered = filtered.filter(goal => goal.status === filters.status);
    }
    
    // Filtrowanie po przypisanym pracowniku
    if (filters.assignedTo !== 'all') {
      if (filters.assignedTo === 'company') {
        filtered = filtered.filter(goal => !goal.assignedTo);
      } else {
        filtered = filtered.filter(goal => goal.assignedTo === filters.assignedTo);
      }
    }
    
    // Filtrowanie po typie celu (firmowy/indywidualny)
    if (filters.onlyCompanyGoals) {
      filtered = filtered.filter(goal => !goal.assignedTo);
    }
    
    if (filters.onlyIndividualGoals) {
      filtered = filtered.filter(goal => !!goal.assignedTo);
    }
    
    // Sortowanie po roku i miesiącu (malejąco)
    filtered.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      
      // Jeśli cele są miesięczne
      if (a.month && b.month) return b.month - a.month;
      
      // Jeśli cele są kwartalne
      if (a.quarter && b.quarter) return b.quarter - a.quarter;
      
      // Jeśli jeden cel jest miesięczny, a drugi kwartalny lub roczny
      if (a.month && !b.month) return -1;
      if (!a.month && b.month) return 1;
      
      // Jeśli jeden cel jest kwartalny, a drugi roczny
      if (a.quarter && !b.quarter) return -1;
      if (!a.quarter && b.quarter) return 1;
      
      return 0;
    });
    
    setFilteredGoals(filtered);
    // Reset paginacji przy zmianie filtrów
    setPage(0);
  };

  // Obsługa paginacji
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Obliczanie procentu realizacji
  const calculateProgress = (current, target) => {
    if (!target || target <= 0) return 0;
    return Math.min(Math.round((current / target) * 100), 100);
  };

  // Obsługa dodawania/edycji celu
  const handleSaveGoal = (goalData) => {
    // Odśwież listę celów
    fetchGoals();
    
    // Zamknij dialog
    setFormDialogOpen(false);
    
    setSnackbar({
      open: true,
      message: selectedGoal ? 'Cel został zaktualizowany' : 'Nowy cel został dodany',
      severity: 'success'
    });
    
    // Wyczyść wybrany cel
    setSelectedGoal(null);
  };

  // Obsługa usuwania celu
  const handleDeleteGoal = async () => {
    if (!selectedGoal) return;
    
    try {
      await deleteDoc(doc(db, 'goals', selectedGoal.id));
      
      // Odśwież listę celów
      fetchGoals();
      
      // Zamknij dialogi
      setDeleteDialogOpen(false);
      setDetailsDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Cel został usunięty',
        severity: 'success'
      });
      
      // Wyczyść wybrany cel
      setSelectedGoal(null);
    } catch (error) {
      console.error('Błąd podczas usuwania celu:', error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas usuwania celu',
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Cele sprzedażowe
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Przełącznik widoku */}
          <Button
            variant="outlined"
            onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}
            startIcon={viewMode === 'cards' ? <ListIcon /> : <GridIcon />}
          >
            {viewMode === 'cards' ? 'Widok tabeli' : 'Widok kart'}
          </Button>
          
          {/* Przycisk dodawania */}
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedGoal(null);
                setFormDialogOpen(true);
              }}
              sx={{
                background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
                color: 'black'
              }}
            >
              Dodaj nowy cel
            </Button>
          )}
        </Box>
      </Box>

      {/* Zakładki */}
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label="Lista celów" />
        {isAdmin && <Tab label="Podsumowanie" />}
      </Tabs>

      {/* Zawartość zakładek */}
      {activeTab === 0 ? (
        <>
          {/* Filtry */}
          <GoalsFilters
            filters={filters}
            onFilterChange={setFilters}
            employees={employees}
            isAdmin={isAdmin}
          />

          {/* Ładowanie */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ p: 2, mt: 2 }}>
              {viewMode === 'cards' ? (
                // Widok kart
                <Grid container spacing={3}>
                  {filteredGoals.length === 0 ? (
                    <Grid item xs={12}>
                      <Typography variant="body1" align="center" sx={{ py: 4 }}>
                        Brak celów spełniających kryteria
                      </Typography>
                    </Grid>
                  ) : (
                    // Wyświetlanie celów jako karty
                    filteredGoals
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map(goal => {
                        const progress = calculateProgress(goal.currentValue || 0, goal.targetValue || 0);
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} key={goal.id}>
                            {goal.assignedTo ? (
                              // Indywidualny cel
                              <GoalCard
                                goal={goal}
                                progress={progress}
                                isAdmin={isAdmin}
                                onEdit={isAdmin ? () => {
                                  setSelectedGoal(goal);
                                  setFormDialogOpen(true);
                                } : null}
                                onDelete={isAdmin ? () => {
                                  setSelectedGoal(goal);
                                  setDeleteDialogOpen(true);
                                } : null}
                                onDetails={() => {
                                  setSelectedGoal(goal);
                                  setDetailsDialogOpen(true);
                                }}
                              />
                            ) : (
                              // Cel firmowy
                              <TeamGoalCard
                                goal={goal}
                                progress={progress}
                                isAdmin={isAdmin}
                                onEdit={isAdmin ? () => {
                                  setSelectedGoal(goal);
                                  setFormDialogOpen(true);
                                } : null}
                                onDelete={isAdmin ? () => {
                                  setSelectedGoal(goal);
                                  setDeleteDialogOpen(true);
                                } : null}
                                onDetails={() => {
                                  setSelectedGoal(goal);
                                  setDetailsDialogOpen(true);
                                }}
                              />
                            )}
                          </Grid>
                        );
                      })
                  )}
                </Grid>
              ) : (
                // Widok tabeli
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body1" align="center" sx={{ py: 4 }}>
                    Widok tabeli zostanie zaimplementowany w przyszłości
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </>
      ) : isAdmin ? (
        <GoalsDashboard 
          goals={goals} 
          employees={employees}
        />
      ) : null}

      {/* Snackbar do powiadomień */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog formularza dodawania/edycji celu - tylko dla admina */}
      {isAdmin && (
        <GoalForm
          open={formDialogOpen}
          onClose={() => {
            setFormDialogOpen(false);
            setSelectedGoal(null);
          }}
          onSave={handleSaveGoal}
          goal={selectedGoal}
          employees={employees}
          isAdmin={isAdmin}
          currentUser={currentUser}
        />
      )}

      {/* Dialog szczegółów celu */}
      <GoalDetailsModal
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        goal={selectedGoal}
        isAdmin={isAdmin}
        onEdit={isAdmin ? () => {
          setDetailsDialogOpen(false);
          setFormDialogOpen(true);
        } : null}
        onDelete={isAdmin ? () => {
          setDetailsDialogOpen(false);
          setDeleteDialogOpen(true);
        } : null}
      />

      {/* Dialog potwierdzenia usunięcia */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Potwierdź usunięcie
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Czy na pewno chcesz usunąć ten cel? Ta operacja jest nieodwracalna.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleDeleteGoal} color="error" autoFocus>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoalsPage;
